import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import clsx from 'clsx';
import CssBaseline from '@mui/material/CssBaseline';
import Drawer from '@mui/material/Drawer';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import { AuthStore } from '@shared/stores';
import Lazyload from '@shared/components/LazyLoad';
import { Navigate, Route, Routes, RouteProps, useLocation } from 'react-router-dom';
import container from '@core/di';
import ROUTES from '@Admin/shared/constants/routes';
import ProfileMenu from './submodules/TopBar/components/ProfileMenu';
import NavigationMenu from './submodules/NavigationMenu';
import { useStyles } from './Private.styles';
import ClinicianStore from '@shared/stores/clinician';

const Families = React.lazy(() => import('./submodules/Families'));
const FamilyTreatmentPlanReview = React.lazy(
  () =>
    import('./submodules/Families/pages/FamilyInfo/components/ApprovedTreatmentPlans/components/ReviewApprovedPlan/ReviewApprovedPlan'),
);
const Materials = React.lazy(() => import('./submodules/Materials'));
const Employee = React.lazy(() => import('./submodules/Employee'));
const Signout = React.lazy(() => import('./submodules/Signout'));
const Profile = React.lazy(() => import('./submodules/Profile'));
const Assessments = React.lazy(() => import('./submodules/Assessments'));
const ContentManagement = React.lazy(() => import('./submodules/ContentManagement'));
const SubscriptionManagement = React.lazy(() => import('./submodules/SubscriptionManagement'));
const NewClientAssignments = React.lazy(() => import('./submodules/NewClientAssignments'));

const ReviewAssessment = React.lazy(
  () =>
    import('./submodules/AssessmentTreatmentPlanReview/AssessmentTreatmentPlanContainer/components/ReviewAssessment/ReviewAssessment'),
);
const AssessmentTreatmentPlanReview = React.lazy(
  () => import('./submodules/AssessmentTreatmentPlanReview'),
);
const AssessmentSummary = React.lazy(
  () =>
    import('./submodules/Families/pages/FamilyInfo/components/AssessmentsTab/AssessmentSummary/AssessmentSummary'),
);
const StartAssessment = React.lazy(
  () =>
    import('./submodules/Families/pages/FamilyInfo/components/AssessmentsTab/StartAssessment/StartAssessment'),
);
const ReviewTreatmentPlan = React.lazy(
  () =>
    import('./submodules/AssessmentTreatmentPlanReview/AssessmentTreatmentPlanContainer/components/ReviewTreatmentPlan/ReviewTreatmentPlan'),
);
const RegenerateTreatmentPlan = React.lazy(
  () =>
    import('./submodules/AssessmentTreatmentPlanReview/AssessmentTreatmentPlanContainer/components/RegeneratedTreatmentPlan/RegeneratedTreatmentPlan'),
);
const ShowDetailsTreatmentPlan = React.lazy(
  () =>
    import('./submodules/AssessmentTreatmentPlanReview/AssessmentTreatmentPlanContainer/components/ShowDetailsTreatmentPlan/ShowDetailsTreatmentPlan'),
);
const Patients = React.lazy(() => import('./submodules/Patients'));

const routes = (): Array<RouteProps> => {
  const {
    employee,
    families,
    familyTreatmentPlanReview,
    materials,
    signoutOidc,
    profile,
    assessments,
    contentTabs,
    subscription,
    client,
    assessmentTreatmentTabs,
    assessmentTreatmentReview,
    assessmentTreatmentPlanReview,
    assessmentSummary,
    startAssessment,
    assessmentTreatmentRegeneration,
    assessmentTreatmentShowDetails,
    patients,
  } = ROUTES.private;

  return [
    { path: `${employee}/*`, element: <Employee /> },
    { path: `${families}/*`, element: <Families /> },
    { path: familyTreatmentPlanReview, element: <FamilyTreatmentPlanReview /> },
    { path: materials, element: <Materials /> },
    { path: signoutOidc, element: <Signout /> },
    { path: `${profile}/*`, element: <Profile /> },
    { path: `${assessments}/*`, element: <Assessments /> },
    { path: `${contentTabs}/*`, element: <ContentManagement /> },
    { path: subscription, element: <SubscriptionManagement /> },
    { path: `${client}/*`, element: <NewClientAssignments /> },
    { path: assessmentTreatmentReview, element: <ReviewAssessment /> },
    { path: assessmentTreatmentPlanReview, element: <ReviewTreatmentPlan /> },
    { path: assessmentSummary, element: <AssessmentSummary /> },
    { path: startAssessment, element: <StartAssessment /> },
    { path: `${assessmentTreatmentTabs}/*`, element: <AssessmentTreatmentPlanReview /> },
    { path: assessmentTreatmentRegeneration, element: <RegenerateTreatmentPlan /> },
    { path: assessmentTreatmentShowDetails, element: <ShowDetailsTreatmentPlan /> },
    { path: `${patients}/*`, element: <Patients /> },
  ];
};

// not sure why this is needed
const hiddenSidebarRoutes = [ROUTES.private.assessmentTreatmentReview];
// it definitely isn't necessary here
const visibleSidebarRoutes = [ROUTES.private.families];
const urlMaxLength = 42;

// not sure why this is needed
const isHiddenBasedOnUrlLength = (url: string) => {
  return visibleSidebarRoutes.some(route => url.startsWith(route))
    ? false
    : url.length >= urlMaxLength;
};

const modules = authStore => {
  return (
    <Lazyload>
      <Routes>
        {routes().map(routeProps => (
          <Route key={String(routeProps.path)} {...routeProps} />
        ))}
        <Route path="*" element={<Navigate to={authStore.defaultRoute} replace />} />
      </Routes>
    </Lazyload>
  );
};

const PrivateModule = observer(() => {
  const location = useLocation();
  const classes = useStyles();
  const authStore = container.get<AuthStore>(AuthStore.diToken);
  const [isHidenMenu, setIsHiddenMenu] = useState(false);
  const clinicianStore = container.get<ClinicianStore>(ClinicianStore.diToken);

  useEffect(() => {
    const isHidden = hiddenSidebarRoutes.includes(location.pathname);
    setIsHiddenMenu(isHidden);
  }, [location.pathname]);

  useEffect(() => {
    if (authStore.profile && authStore.profile.profile) {
      if (
        authStore.profile.profile.role === 'MasterClinician' ||
        authStore.profile.profile.role === 'SeniorClinician' ||
        authStore.profile.profile.role === 'Clinician'
      ) {
        clinicianStore.updateNotAssignedAssessmentsInfo();
        clinicianStore.getAssessmentsTreatmentPlansInfo();
      } else {
        clinicianStore.notAssignedAssessmentCount = 0;
        clinicianStore.totalNotApprovedEntitiesCount = 0;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  return (
    <div className={classes.root}>
      <CssBaseline />
      {!isHidenMenu && !isHiddenBasedOnUrlLength(location.pathname) && (
        <AppBar position="absolute" className={clsx(classes.appBar)}>
          <Toolbar className={classes.toolbar}>
            <Typography
              component="h1"
              variant="h6"
              color="inherit"
              noWrap
              className={classes.title}
            >
              Family Hope Center
            </Typography>
            <ProfileMenu />
          </Toolbar>
        </AppBar>
      )}
      {!isHidenMenu && !isHiddenBasedOnUrlLength(location.pathname) && (
        <Drawer
          variant="permanent"
          classes={{
            paper: clsx(classes.drawerPaper, classes.drawerAnchor),
          }}
        >
          <div className={classes.topPadding} />
          <NavigationMenu
            notAssignedAssessmentCount={clinicianStore.notAssignedAssessmentCount}
            totalNotApprovedEntitiesCount={clinicianStore.totalNotApprovedEntitiesCount}
          />
        </Drawer>
      )}
      {!isHidenMenu && !isHiddenBasedOnUrlLength(location.pathname) ? (
        <>
          <main className={classes.content}>
            <div className={classes.appBarSpacer} />
            <Container maxWidth={false} className={classes.container}>
              {modules(authStore)}
            </Container>
          </main>
        </>
      ) : (
        <>
          <main className={classes.content}>{modules(authStore)}</main>
        </>
      )}
    </div>
  );
});

export default PrivateModule;
